import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './voucher.css';
import { INSTANCE_WHITE_LOGO_URL as logo } from '../../components/ui/InstanceLogo';
import chip from '../../assets/images/chip.png';
import Message from '../../components/ui/Message';
import { MenuQRContext, savePendingCart, clearPendingCart } from '../../context/menu/MenuQRContext';

// Pago con tarjeta del pedido del menú QR (comercios con menuQrOrderOnly = false).
// La orden se crea en el servidor solo si el pago se aprueba; luego se muestra el número de orden.

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const getCardBrand = (number: string): string => {
    if (number.startsWith('4')) return 'VISA';
    if (/^5[1-5]/.test(number) || /^2[2-7]/.test(number)) return 'MasterCard';
    if (/^3[47]/.test(number)) return 'American Express';
    return '';
};

// Vista previa con los dígitos del medio ocultos
const formatCardPreview = (number: string): string => {
    if (/^3[47]/.test(number)) {
        const masked = number.length > 10 ? number.slice(0, 4) + '******' + number.slice(10) : number;
        return masked.padEnd(15, '*').replace(/^(.{4})(.{6})(.*)$/, '$1 $2 $3');
    }
    const masked = number.length > 12 ? number.slice(0, 4) + '********' + number.slice(12) : number;
    return masked.padEnd(16, '*').replace(/(.{4})/g, '$1 ').trim();
};

// Luhn: detecta errores de digitación antes de enviar al banco
const isValidLuhn = (number: string): boolean => {
    let sum = 0;
    let double = false;
    for (let i = number.length - 1; i >= 0; i--) {
        let digit = Number(number[i]);
        if (double) { digit *= 2; if (digit > 9) digit -= 9; }
        sum += digit;
        double = !double;
    }
    return number.length >= 13 && sum % 10 === 0;
};

const isValidExpiration = (mmyy: string): boolean => {
    if (!/^\d{4}$/.test(mmyy)) return false;
    const month = Number(mmyy.slice(0, 2));
    const year = 2000 + Number(mmyy.slice(2));
    if (month < 1 || month > 12) return false;
    const now = new Date();
    return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
};

const formatMoney = (amount: number) =>
    '₡ ' + amount.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PayPage: React.FC = () => {
    const { selectedProducts, merchant, token, payOrder, clearProduct } = useContext(MenuQRContext);
    const navigate = useNavigate();
    const location = useLocation();
    const detail: string = (location.state as any)?.detail ?? '';

    const [name, setName] = useState('');
    const [card, setCard] = useState('');
    const [expiration, setExpiration] = useState('');
    const [cvv, setCvv] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Sin pedido (por ejemplo, al recargar la página) se vuelve al menú
    useEffect(() => {
        if (selectedProducts.length === 0) {
            navigate(token ? `/menuqr/${token}` : '/', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const total = useMemo(
        () => selectedProducts.reduce((acc, p) => acc + Number(p.price ?? 0) * Number(p.quantity ?? 0), 0),
        [selectedProducts]
    );

    const brand = getCardBrand(card);
    const errors = {
        name: name.trim().length < 3,
        card: !isValidLuhn(card),
        expiration: !isValidExpiration(expiration),
        cvv: !/^\d{3,4}$/.test(cvv),
    };
    const invalid = (field: keyof typeof errors) => submitted && errors[field] ? 'is-invalid' : '';

    const expirationPreview = expiration.length === 4 ? `${expiration.slice(0, 2)}/${expiration.slice(2)}` : 'MM/YY';

    // Envía al banco el formulario del desafío 3-D Secure (el navegador sale de la app y vuelve a /menuqr/result)
    const submitChallenge = (action: string, fields: Record<string, string | undefined>) => {
        savePendingCart(String(token), selectedProducts, detail);
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = action;
        form.style.display = 'none';
        Object.entries(fields).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value ?? '';
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
    };

    const handlePay = async () => {
        setSubmitted(true);
        if (Object.values(errors).some(Boolean) || isPaying) return;

        setIsPaying(true);
        const result = await payOrder({ card, expiration, cvv, name: name.trim() }, detail);

        if (result.status === 'APPROVED') {
            clearPendingCart();
            clearProduct();
            navigate('/menuqr/result', { replace: true, state: { orderNumber: result.orderNumber, amount: result.amount ?? total, token } });
            return;
        }

        if (result.status === 'CONFIRMED' && result.action) {
            // Se mantiene el indicador de carga mientras el navegador va al banco
            submitChallenge(result.action, { creq: result.creq, threeDSSessionData: result.threeDSSessionData });
            return;
        }

        setIsPaying(false);
        setCvv('');
        setErrorMessage(result.message || 'El pago no fue aprobado. Verifique los datos de la tarjeta.');
    };

    if (isPaying) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-info" role="status" />
                <p className="mt-3 text-muted">Procesando el pago, no cierre esta página...</p>
            </div>
        );
    }

    return (
        <div className="wrapper1">
            <div className="headerLogin">
                <img src={logo} alt="Logo" className="logo" />
            </div>
            <div className="container mt-4" style={{ paddingBottom: '100px', maxWidth: 560 }}>

                <h3 className="mb-3">Pago del pedido</h3>

                {/* Resumen */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                        {merchant?.name && <div className="text-muted small mb-2">{merchant.name}</div>}
                        {selectedProducts.map(p => (
                            <div key={p.id} className="d-flex justify-content-between small mb-1">
                                <span>{p.quantity} x {p.name}</span>
                                <span>{formatMoney(Number(p.price ?? 0) * Number(p.quantity ?? 0))}</span>
                            </div>
                        ))}
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between fw-bold">
                            <span>Total a pagar</span>
                            <span>{formatMoney(total)}</span>
                        </div>
                    </div>
                </div>

                {/* Vista previa de la tarjeta */}
                <div className="d-flex">
                    <div className="card bg-primary text-white p-3 mb-4 card-gradient">
                        <div className="col d-flex justify-content-between align-items-center">
                            <h5>{brand}</h5>
                            <img src={chip} alt="chip" height={30} width={33} />
                        </div>
                        <div style={{ height: '30px' }}></div>
                        <h5>{formatCardPreview(card)}</h5>
                        <p style={{ margin: 0, textAlign: 'right', paddingRight: '20px' }}>Valid Thru {expirationPreview}</p>
                        <p style={{ margin: 0 }}>{name ? name.toUpperCase() : 'Nombre tarjeta'}</p>
                    </div>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handlePay(); }} noValidate>
                    <div className="mb-3">
                        <label htmlFor="payName" className="form-label">Nombre en la tarjeta</label>
                        <input type="text" className={`form-control ${invalid('name')}`} id="payName" autoComplete="cc-name"
                            value={name} onChange={(e) => setName(e.target.value)} />
                        <div className="invalid-feedback">Ingrese el nombre como aparece en la tarjeta.</div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="payCard" className="form-label">Número de tarjeta</label>
                        <input type="text" inputMode="numeric" className={`form-control ${invalid('card')}`} id="payCard" autoComplete="cc-number"
                            maxLength={19} value={card} onChange={(e) => setCard(onlyDigits(e.target.value))} />
                        <div className="invalid-feedback">El número de tarjeta no es válido.</div>
                    </div>

                    <div className="row">
                        <div className="col-6 mb-3">
                            <label htmlFor="payExpiration" className="form-label">Vencimiento (MMYY)</label>
                            <input type="text" inputMode="numeric" className={`form-control ${invalid('expiration')}`} id="payExpiration" autoComplete="cc-exp"
                                maxLength={4} placeholder="MMYY" value={expiration} onChange={(e) => setExpiration(onlyDigits(e.target.value))} />
                            <div className="invalid-feedback">Fecha inválida o vencida.</div>
                        </div>
                        <div className="col-6 mb-3">
                            <label htmlFor="payCvv" className="form-label">CVV</label>
                            <input type="password" inputMode="numeric" className={`form-control ${invalid('cvv')}`} id="payCvv" autoComplete="cc-csc"
                                maxLength={4} value={cvv} onChange={(e) => setCvv(onlyDigits(e.target.value))} />
                            <div className="invalid-feedback">CVV inválido.</div>
                        </div>
                    </div>

                    <p className="text-muted small">
                        <i className="bi bi-shield-lock me-1" />
                        Su banco puede pedirle una verificación adicional. La orden se envía al comercio cuando el pago se aprueba.
                    </p>

                    {/* Envío con Enter */}
                    <button type="submit" className="d-none" aria-hidden="true" tabIndex={-1} />
                </form>
            </div>

            {/* Botones fijos abajo */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: '#fff',
                padding: '10px',
                boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
                zIndex: 999
            }} className="d-flex justify-content-center">
                <button type="button" onClick={() => navigate(-1)} className="btn btn-default w-75">
                    Atrás
                </button>
                <div className='me-5' />
                <button type="button" onClick={handlePay} className="btn btn-primary w-75">
                    Pagar {formatMoney(total)}
                </button>
            </div>

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} />
            )}
        </div>
    );
};

export default PayPage;
